import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  Icon,
  FormControl
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { Formik } from "formik";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import Dialog from "../../../waybee-ui/Dialog/Dialog";
import Button from "../../../waybee-ui/Button";
import TeacherService from "../../../services/TeacherService";

const FormGrid = styled(Grid)`
  overflow: scroll;

  @media (min-height: 800px) {
    scrollbar-width: none;
  }
`;

const Content = styled(DialogContent)`
  overflow: hidden;
`;

const StudentName = styled(Typography)`
  font-size: 20px;
`;

const StudentRa = styled(Typography)`
  font-size: 16px;
  opacity: 0.5;
  color: #000000;
`;

const AttachmentText = styled(Typography)`
  margin-top: 20px;
`;

const AttachmentButton = styled(Button)`
  margin-top: 16px;
`;

const LoadingContainer = styled.div`
  margin: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const HiddenContainer = styled.div`
  display: none;
`;

const NewOccurrenceDialog = ({ open, handleClose, studentId, studentName }) => {
  const [dateSelected, setDateSelected] = useState(null);
  const [hasClassAttached, setHasClassAttached] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [disciplines, setDisciplines] = useState(null);
  const [occurrenceTypes, setOccurrenceTypes] = useState([]);
  const [occurrenceStages, setOccurrenceStages] = useState([]);
  const [loadingOnSave, setLoadingOnSave] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [occurFiles, setOccurFiles] = useState([]);

  const formatSelectedDate = schedule => {
    const [hour, minute] = schedule.startTime.split(":");
    return moment(schedule.date)
      .set({ hour, minute })
      .format("YYYY-MM-DD HH:mm:SS");
  };

  const formatData = data => {
    const {
      discipline,
      schedule,
      occurrenceType,
      obs,
      occurrenceStage,
      reason
    } = data;
    const files = [];
    const dateSchedule = !dateSelected
      ? formatSelectedDate(schedule)
      : dateSelected;
    const idType = reason.id.split("-")[0];

    occurFiles.map(occurFile =>
      files.push({
        name: occurFile.attachmentName,
        content: occurFile.attachmentContent
      })
    );

    return {
      absences: [],
      idTurmaDisc: discipline ? discipline.idTurmaDisc : schedule.idTurmaDisc,
      idPerlet: discipline ? discipline.idPerlet : schedule.idPerlet,
      date: dateSchedule,
      occurrences: [
        {
          description: obs,
          type: reason.name,
          course: discipline.name,
          idGroup: occurrenceType.idGroup,
          idType,
          files,
          studentId,
          codEtapa: occurrenceStage.id,
          notificationPermission: occurrenceType.notificationPermission,
          title: occurrenceType.name
        }
      ],
      removed: []
    };
  };

  const filterTeacherDiscipline = schedule => {
    const filteredDisciplines = schedule.reduce(
      (uniqueDisciplines, currentItem) => {
        const isUnique = !uniqueDisciplines.some(
          item => item.codDisc === currentItem.codDisc
        );
        if (isUnique) {
          uniqueDisciplines.push(currentItem);
        }
        return uniqueDisciplines;
      },
      []
    );

    return filteredDisciplines.map(discipline => ({
      id: discipline.codDisc,
      codDisc: discipline.codDisc,
      name: discipline.name,
      idPerlet: discipline.idPerLet,
      idTurmaDisc: discipline.idTurmaDisc
    }));
  };

  const getSchedules = useCallback(async () => {
    const classId = localStorage.getItem("class");
    setLoadingContent(true);

    try {
      const classSchedule = await TeacherService.getScheduleForOccurrence(
        classId
      );

      if (classSchedule && !classSchedule[0].idTurmaDisc)
        setHasClassAttached(false);

      const filteredDisciplines = filterTeacherDiscipline(classSchedule);

      setDisciplines(filteredDisciplines);
      setSchedules(classSchedule);
      setLoadingContent(false);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const getOccurrenceTypes = async lesson => {
    const classId = localStorage.getItem("class");
    const types = await TeacherService.getOccurrenceTypes(
      classId,
      lesson.idTurmaDisc,
      lesson.codDisc
    );

    setOccurrenceTypes(types);
  };

  const getOccurrencesStages = async data => {
    const classId = localStorage.getItem("class");
    const date = dateSelected ? moment(dateSelected) : moment(data.date);
    const codPerlet = date.year();

    const stages = await TeacherService.getOccurrenceStages(
      classId,
      codPerlet,
      data.codDisc
    );

    setOccurrenceStages(stages);
  };

  const insertOccurrence = async data => {
    const filteredData = data;

    if (!hasClassAttached) {
      filteredData.schedule = schedules;
    }

    const occurData = formatData(filteredData);
    setLoadingOnSave(true);
    await TeacherService.saveOccurrence(occurData).then(() =>
      handleClose(true)
    );
  };

  const handleFileSelect = (e, setFieldValue) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const reader = new FileReader();

        reader.onload = event => {
          const fileData = {
            id: file.lastModified + file.size,
            name: file.name,
            content: event.target.result.split(",")[1]
          };

          occurFiles.push({
            attachmentName: fileData.name,
            attachmentContent: fileData.content
          });

          if (occurFiles) {
            setFieldValue("attachmentName", [...occurFiles, fileData.name]);
            setFieldValue("attachmentData", [...occurFiles, fileData.content]);
          } else {
            setFieldValue("attachmentName", [fileData.name]);
            setFieldValue("attachmentData", [fileData.content]);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const deleteAttachment = occurFile => {
    const files = _.filter(
      occurFiles,
      file =>
        file.attachmentName !== occurFile.attachmentName &&
        file.attchmentContent !== occurFile.attachmentContent
    );
    setOccurFiles(files);
  };

  const setSelectedDiscipline = discipline => {
    const filteredData = { ...schedules[0], ...discipline };

    getOccurrenceTypes(filteredData);
    getOccurrencesStages(filteredData);
  };

  useEffect(() => {
    getSchedules();
  }, [getSchedules]);

  const handleDateSelection = date => {
    const formatedDate = moment(date).format("YYYY-MM-DD HH:mm:SS");
    setDateSelected(formatedDate);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Dialog open={open} onClose={handleClose} fullWidth closeButton>
        <DialogTitle>Nova Ocorrência</DialogTitle>
        <Formik initialValues={{}}>
          {({ values, handleChange, setFieldValue }) => (
            <FormGrid>
              <Content>
                <StudentName color="secondary">{studentName}</StudentName>
                <StudentRa>
                  R.A:
                  {studentId}
                </StudentRa>
                <Grid container>
                  <Grid container spacing={2}>
                    {loadingContent ? (
                      <LoadingContainer>
                        <CircularProgress />
                      </LoadingContainer>
                    ) : (
                      <Grid item xs={12}>
                        <HiddenContainer>
                          <FormControl fullWidth margin="normal">
                            <InputLabel id="schedule-label">Aula</InputLabel>
                            <Select
                              id="schedule"
                              name="schedule"
                              labelId="schedule-label"
                              value={values.schedule ? values.schedule : ""}
                              fullWidth
                              onChange={e => {
                                handleChange(e);
                                getOccurrenceTypes(e.target.value);
                                getOccurrencesStages(e.target.value);
                              }}
                            >
                              {schedules.map(lesson => (
                                <MenuItem key={lesson.id} value={lesson}>
                                  {lesson.name}
                                  {' '}
                                  -
                                  {" "}
                                  {moment(lesson.date).format("DD/MM")}
                                  {" "}
                                  {lesson.startTime}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </HiddenContainer>
                        <FormControl fullWidth margin="normal">
                          <InputLabel id="schedule-label">
                            Disciplina
                          </InputLabel>
                          <Select
                            id="discipline"
                            name="discipline"
                            labelId="discipline-label"
                            value={values.discipline}
                            fullWidth
                            onChange={e => {
                              handleChange(e);
                              setSelectedDiscipline(e.target.value);
                            }}
                          >
                            {disciplines.map(discipline => (
                              <MenuItem key={discipline.id} value={discipline}>
                                {discipline.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <KeyboardDatePicker
                          fullWidth
                          disableToolbar
                          format="dd/MM/yyyy"
                          margin="normal"
                          id="date-picker-inline"
                          label="Data"
                          value={dateSelected}
                          onChange={handleDateSelection}
                          placeholder="DD/MM/YYYY"
                        />
                        {(values.schedule || values.discipline) &&
                          dateSelected && (
                            <>
                              <FormControl fullWidth margin="normal">
                                <InputLabel id="occurrenceStage-label">
                                  Etapa
                                </InputLabel>
                                <Select
                                  id="occurrenceStage-label"
                                  name="occurrenceStage"
                                  value={values.occurrenceStage}
                                  onChange={handleChange}
                                  fullWidth
                                >
                                  {occurrenceStages.map(occurrenceStage => (
                                    <MenuItem
                                      key={occurrenceStage.id}
                                      value={occurrenceStage}
                                    >
                                      {occurrenceStage.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl fullWidth margin="normal">
                                <InputLabel id="occurrenceType-label">
                                  Grupos de ocorrência
                                </InputLabel>
                                <Select
                                  id="occurrenceType-label"
                                  name="occurrenceType"
                                  value={values.occurrenceType}
                                  onChange={handleChange}
                                  fullWidth
                                >
                                  {occurrenceTypes.map(occurrenceType => (
                                    <MenuItem
                                      key={occurrenceType.id}
                                      value={occurrenceType}
                                    >
                                      <Icon>{occurrenceType.icon}</Icon>
                                      {occurrenceType.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </>
                          )}
                        {values.occurrenceType && (
                          <>
                            <FormControl fullWidth margin="normal">
                              <InputLabel id="reason-label">
                                Tipos da ocorrência
                              </InputLabel>
                              <Select
                                fullWidth
                                name="reason"
                                id="reason-label"
                                value={values.reason}
                                onChange={handleChange}
                              >
                                {values.occurrenceType.reasons.map(reason => (
                                  <MenuItem key={reason.id} value={reason}>
                                    {reason.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </>
                        )}
                        {values.reason && (
                          <>
                            <TextField
                              onChange={handleChange}
                              name="obs"
                              id="obs"
                              label="Observações"
                              value={values.obs}
                              fullWidth
                              rowsMax="7"
                              multiline
                            />
                            <label htmlFor="files-container">
                              <AttachmentButton
                                variant="contained"
                                color="primary"
                                component="span"
                                startIcon={<AttachFileIcon />}
                                fullwidth
                              >
                                Adicionar anexo
                              </AttachmentButton>
                              <input
                                accept="*"
                                id="files-container"
                                name="files-container"
                                multiple
                                type="file"
                                style={{ display: "none" }}
                                onChange={e => {
                                  handleFileSelect(e, setFieldValue);
                                  e.target.value = null;
                                }}
                              />
                            </label>
                            {occurFiles.length !== 0 && (
                              <>
                                <AttachmentText
                                  variant="body1"
                                  color="textSecondary"
                                >
                                  Anexos:
                                </AttachmentText>
                                {occurFiles.map(attch => (
                                  <Chip
                                    onDelete={() => {
                                      deleteAttachment(attch);
                                    }}
                                    key={attch.attachmentContent}
                                    icon={<AttachFileIcon />}
                                    label={attch.attachmentName}
                                  />
                                ))}
                              </>
                            )}
                          </>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Content>
              <DialogActions>
                {loadingOnSave ? (
                  <CircularProgress />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => insertOccurrence(values)}
                  >
                    Salvar
                  </Button>
                )}
              </DialogActions>
            </FormGrid>
          )}
        </Formik>
      </Dialog>
    </MuiPickersUtilsProvider>
  );
};

NewOccurrenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  studentId: PropTypes.string.isRequired,
  studentName: PropTypes.string.isRequired
};

export default NewOccurrenceDialog;
